import ApiBuilder from "@/api/ApiBuilder";
import { BillingCycle, SelectablePlan } from "@/types/monetisationTypes";
import { createAsyncThunk } from "@reduxjs/toolkit";

/**
 * Monetisation thunks.
 *
 * Every callback fires on both paths — success with the envelope, failure with
 * the error — because the screens branch on the outcome (a 409 window block and
 * a 202 "payment required" are both things the vendor has to be told about, not
 * silent failures).
 */

interface VenueArg {
    venueId: string;
    callback?: (response: any) => void;
}

interface CycleArg extends VenueArg {
    billingCycle: BillingCycle;
}

interface SwitchArg extends VenueArg {
    toPlan: SelectablePlan;
    billingCycle?: BillingCycle;
    reason?: string;
}

const failure = (error: any, fallback: string) =>
    error?.error || error?.response?.data?.message || error?.message || fallback;

export const getMonetisationPlans = createAsyncThunk<any, { callback?: (res: any) => void } | undefined>(
    "monetisation/getPlans",
    async (arg, { rejectWithValue }) => {
        try {
            const response = await ApiBuilder.getMonetisationPlans();
            arg?.callback?.(response);
            return response;
        } catch (error) {
            arg?.callback?.(error);
            return rejectWithValue(failure(error, "Could not load the plans"));
        }
    }
);

export const getVenueMonetisation = createAsyncThunk<any, VenueArg>(
    "monetisation/getVenuePlan",
    async ({ venueId, callback }, { rejectWithValue }) => {
        try {
            const response = await ApiBuilder.getVenueMonetisation(venueId);
            callback?.(response);
            return response;
        } catch (error) {
            callback?.(error);
            return rejectWithValue(failure(error, "Could not load this venue's plan"));
        }
    }
);

/** Cycle price on its own. For a venue that is not live yet use getGoLiveQuote. */
export const getSubscriptionQuote = createAsyncThunk<any, CycleArg>(
    "monetisation/getQuote",
    async ({ venueId, billingCycle, callback }, { rejectWithValue }) => {
        try {
            const response = await ApiBuilder.getSubscriptionQuote(venueId, { billingCycle });
            callback?.(response);
            return response;
        } catch (error) {
            callback?.(error);
            return rejectWithValue(failure(error, "Could not price that billing cycle"));
        }
    }
);

/** Listing fee included — this is the one with payTodayPaise. */
export const getGoLiveQuote = createAsyncThunk<any, VenueArg>(
    "monetisation/getGoLiveQuote",
    async ({ venueId, callback }, { rejectWithValue }) => {
        try {
            const response = await ApiBuilder.getGoLiveQuote(venueId);
            callback?.(response);
            return response;
        } catch (error) {
            callback?.(error);
            return rejectWithValue(failure(error, "Could not load the go-live quote"));
        }
    }
);

/**
 * Creates the Razorpay subscription. The venue does NOT move to SUBSCRIPTION
 * here — that happens on the subscription.charged webhook, so the caller has to
 * poll getVenueMonetisation after checkout closes.
 */
export const subscribeVenue = createAsyncThunk<any, CycleArg>(
    "monetisation/subscribe",
    async ({ venueId, billingCycle, callback }, { rejectWithValue }) => {
        try {
            const response = await ApiBuilder.subscribeVenue(venueId, { billingCycle });
            callback?.(response);
            return response;
        } catch (error) {
            callback?.(error);
            return rejectWithValue(failure(error, "Could not start the subscription"));
        }
    }
);

/**
 * Called when the Razorpay checkout is closed without an authorisation. Clears
 * the pending mandate so it does not block the next attempt. The server
 * re-checks Razorpay first, so a mandate that did go through comes back as
 * ACTIVATED / AUTHORISED instead of being torn down.
 */
export const abandonAuthorisation = createAsyncThunk<any, VenueArg & { reason?: string }>(
    "monetisation/abandonAuthorisation",
    async ({ venueId, reason, callback }, { rejectWithValue }) => {
        try {
            const response = await ApiBuilder.abandonAuthorisation(venueId, { reason });
            callback?.(response);
            return response;
        } catch (error) {
            callback?.(error);
            return rejectWithValue(failure(error, "Could not clear the pending payment"));
        }
    }
);

/**
 * Three success shapes, told apart by `payload.outcome`:
 * PAYMENT_REQUIRED (202) — call subscribeVenue next; nothing has changed yet.
 * SCHEDULED — auto-debit is already off, the flip lands at effectiveAt.
 * SWITCHED — done, effective now.
 * A 409 means the yearly switch window is not open; the error text says when.
 */
export const switchVenuePlan = createAsyncThunk<any, SwitchArg>(
    "monetisation/switchPlan",
    async ({ venueId, toPlan, billingCycle, reason, callback }, { rejectWithValue }) => {
        try {
            const response = await ApiBuilder.switchVenuePlan(venueId, { toPlan, billingCycle, reason });
            callback?.(response);
            return response;
        } catch (error) {
            callback?.(error);
            return rejectWithValue(failure(error, "Could not switch the plan"));
        }
    }
);

/**
 * Cancels a scheduled switch. Note this does NOT restore auto-debit: when the
 * response carries mandateNeedsReauthorisation the vendor has to go back
 * through subscribeVenue or the subscription lapses at period end anyway.
 */
export const cancelScheduledPlanSwitch = createAsyncThunk<any, VenueArg>(
    "monetisation/cancelScheduledSwitch",
    async ({ venueId, callback }, { rejectWithValue }) => {
        try {
            const response = await ApiBuilder.cancelScheduledPlanSwitch(venueId);
            callback?.(response);
            return response;
        } catch (error) {
            callback?.(error);
            return rejectWithValue(failure(error, "Could not cancel the scheduled switch"));
        }
    }
);
